import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AnimeGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/anime-generator',
			name: 'AI Anime Generator | Lift',
			description:
				'Generate stunning AI anime art from your photos in seconds. No watermark. Create yours with Lift now.',
			url: 'https://liftapp.ai/anime-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/anime-generator#product' },
				{ '@id': 'https://liftapp.ai/anime-generator#howto' },
				{ '@id': 'https://liftapp.ai/anime-generator#benefits' },
				{ '@id': 'https://liftapp.ai/anime-generator#faq' },
				{ '@id': 'https://liftapp.ai/anime-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/anime-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/anime-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/anime-generator#product',
			name: 'AI Anime Generator',
			description:
				'Generate stunning AI anime art from your photos in seconds. No watermark. Create yours with Lift now.',
			url: 'https://liftapp.ai/anime-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/anime-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: 'Choose a photo from your device or take one using Lift’s app',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'We create a custom AI style',
					text: 'Our AI takes your photo, keeps your unique look, and transforms it into high-quality anime art. From sharp details to expressive vibes — you’ll get a flawless, authentic anime version of yourself.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/anime-generator#benefits',
			name: 'Who can benefit from AI Anime Generator?',
			description:
				'AI Anime Generator helps users transform their photos into stunning anime art. Ideal for creators, cosplayers, couples, and personal branding.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Perfect for anyone who dreams of seeing themselves as an anime character',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Cosplayers can preview character-inspired looks without a costume',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators and streamers can get eye-catching avatars for Twitch, YouTube, or social media',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Friends and couples can create fun, shareable anime portraits together',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Quick, creative, and premium-quality — ideal for personal fun or online branding',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.148840af.webp',
				name: 'Smiling anime girl in kimono',
				description: 'Anime transformation example with kimono and flames',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/anime-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Anime Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Anime Generator is an online tool that transforms your selfies into detailed anime-style portraits — perfect for avatars, social media, or creative projects.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Anime Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI analyzes your photo with deep learning, keeps your facial features accurate, and reimagines them in anime style with artistic backgrounds, colors, and details. Everything is automatic — no manual editing needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep my face and details recognizable?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Anime Generator uses advanced face-mapping so your anime version looks just like you — only in a stylized, hand-drawn form. Your hair, eyes, and expressions stay consistent and true.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/anime-generator#why-lift',
			name: 'Why Lift’s AI Anime Generator stands out',
			description:
				'Discover how Lift’s AI-powered Anime Generator delivers professional results with smart, affordable, and cross-platform support.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Professional results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI accuracy',
					description:
						'Smart algorithms deliver enhancements tailored to your specific image',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly editing services with Lift’s affordable solution',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.db565f5c.webp',
				name: 'Anime-style father and son as pirates',
				description:
					'Father and son anime transformation posing like cheerful pirates on a ship',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/anime-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/anime-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Object Remover',
					item: 'https://liftapp.ai/anime-generator',
				},
			],
		},
	],
};
